import Table from "components/molecules/Table";
import TableLayout from "components/molecules/TableLayout";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import downIcon from "../../../../assets/Reports/downIcon.svg";
import upIcon from "../../../../assets/Reports/upIcon.svg";
import LineChart from "./LineChart";
import PieChart from "./PieChart";
import analyticsService from "services/analyticsService";
import Toast from "components/molecules/Toast";

const StyledWeeklyReport = styled.div`
    width: 100%;
    margin-bottom: 40px;

    .charts-wrap {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding: 20px;
        box-shadow: 0px 1px 16px 0px rgba(0, 0, 0, 0.08);
        border-radius: 31px;
        margin-bottom: 30px;

        @media (min-width: 992px) {
            flex-direction: row;
        }

        .lineChartWrap {
            width: 100%;
            padding: 15px 15px 10px;
            border: 1px solid rgba(236, 236, 236, 1);
            border-radius: 10px;
            @media (min-width: 576px) {
                padding: 30px 30px 10px;
            }
            @media (min-width: 992px) {
                width: 50%;
            }
            .chart-title {
                font-size: 20px;
                line-height: 24px;
                font-weight: 400;
                margin-bottom: 10px;
            }
            .chart-desc {
                display: block;
                color: rgba(143, 143, 143, 1);
                font-size: 14px;
                line-height: 18px;
                font-weight: 300;
                margin-bottom: 20px;
            }
        }
    }

    .table-layout {
        border: none;
        box-shadow: 0px 1px 16px 0px rgba(0, 0, 0, 0.08);
        tbody {
            tr {
                border: 0;
                td {
                    padding-top: 20px;
                    padding-bottom: 20px;
                }
            }
        }
    }

    /* Skeleton loader */
    .skeleton-row {
        display: flex;
        gap: 16px;
        padding: 16px 0;
        border-bottom: 1px solid rgba(236, 236, 236, 0.5);
        .skeleton-cell {
            height: 16px;
            border-radius: 8px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.4s infinite;
            flex: 1;
        }
        @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }
    }
`;

/**
 * WeeklyReport
 *
 * Props (all from parent reports.jsx):
 *   restaurantId, storeId, startDate, endDate
 */
const WeeklyReport = ({ restaurantId, storeId, startDate, endDate }) => {
    const [loading, setLoading] = useState(false);
    const [weeklyData, setWeeklyData] = useState(null);
    const [searchQuery, setSearchQuery] = useState({ page: 1, itemsPerPage: 7 });

    // ─── Fetch ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!restaurantId) return;

        const fetchWeekly = async () => {
            setLoading(true);
            try {
                const res = await analyticsService.getWeeklyReport({
                    restaurantId,
                    storeId: storeId || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                });
                setWeeklyData(res?.data || null);
            } catch (err) {
                Toast({ type: "error", message: err.message || "Failed to load weekly report" });
            } finally {
                setLoading(false);
            }
        };

        fetchWeekly();
    }, [restaurantId, storeId, startDate, endDate]);

    const columnNames = ["Customer", "Orders (Week)", "Revenue (Week)", "Growth", "All-Time Revenue"];

    // ─── Chart data ───────────────────────────────────────────────────────────
    const dailyRevenue = weeklyData?.dailyRevenue || [];
    const orderDistribution = weeklyData?.orderDistribution || [];

    // ─── Skeleton ─────────────────────────────────────────────────────────────
    const SkeletonRows = () =>
        Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton-row">
                {Array.from({ length: 5 }).map((__, j) => (
                    <div key={j} className="skeleton-cell" style={{ flex: j === 0 ? 2 : 1 }} />
                ))}
            </div>
        ));

    return (
        <StyledWeeklyReport>
            <div className="charts-wrap">
                <div className="lineChartWrap">
                    <h2 className="chart-title">Daily Revenue Trend</h2>
                    <span className="chart-desc">
                        {loading ? "Loading…" : `${dailyRevenue.length}-day performance overview`}
                    </span>
                    <LineChart data={dailyRevenue} />
                </div>
                <div className="lineChartWrap">
                    <h2 className="chart-title">Store Performance Distribution</h2>
                    <span className="chart-desc">Order status breakdown</span>
                    <PieChart data={orderDistribution} />
                </div>
            </div>

        </StyledWeeklyReport>
    );
};

export default WeeklyReport;
